<template>
    <div v-if="coverImage" class="bk-book" :class="bookClasses" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave" style="width: 277px;height: 370px;margin-bottom:20px">
        <!-- Face avant du livre -->
        <div class="bk-front">
            <div class="bk-cover" :style="coverStyle">
                <div class="bk-cover-content">
                    <h2>
                        <span class="bk-author">{{ author }}</span>
                        <span class="bk-title">{{ title }}</span>
                    </h2>
                </div>
            </div>
            <div class="bk-cover-back"></div>
        </div>

        <!-- Pages intérieures -->
        <div class="bk-page" v-show="showPages">
            <div
                v-for="(page, index) in pages"
                :key="index"
                class="bk-content"
                :class="{ 'bk-content-current': index === currentPage }"
            >
                <div v-html="page"></div>
            </div>

            <!-- Navigation des pages -->
            <nav v-if="pages.length > 1" class="bk-nav">
        <span
            v-for="(page, index) in pages"
            :key="index"
            @click="currentPage = index"
            :class="{ active: index === currentPage }"
        >
          {{ index + 1 }}
        </span>
            </nav>
        </div>

        <!-- Face arrière -->
        <div class="bk-back" :style="backStyle">
            <div class="bk-back-content">
                <img v-if="backImage" :src="backImage" :alt="`${title} back cover`">
                <p v-if="backText">{{ backText }}</p>
            </div>
        </div>

        <!-- Côtés du livre -->
        <div class="bk-right"></div>
        <div class="bk-left" :style="leftSideStyle">
            <h2>
                <span class="bk-author">{{ leftSideAuthor || author }}</span>
                <span class="bk-title">{{ subText(leftSideTitle) || subText(title) }}</span>
            </h2>
        </div>
        <div class="bk-top"></div>
        <div class="bk-bottom"></div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue'

// Props du composant
const props = defineProps({
    title: {
        type: String,
    },
    author: {
        type: String,
    },
    backText: {
        type: String,
        default: ''
    },
    backImage: {
        type: String,
        default: ''
    },

    // Style et couleurs
    bookStyle: {
        type: String,
        default: 'book-1',
        validator: (value) => ['book-1', 'book-2', 'book-3'].includes(value)
    },
    backgroundColor: {
        type: String,
        default: '#ff924a'
    },
    backColor: {
        type: String,
        default: ''
    },
    leftSideColor: {
        type: String,
        default: ''
    },

    // Couverture
    coverImage: {
        type: String,
        default: null
    },
    coverImagePosition: {
        type: String,
        default: 'center center'
    },
    coverImageSize: {
        type: String,
        default: 'contain'
    },
    coverOverlay: {
        type: Boolean,
        default: true
    },
    coverOverlayColor: {
        type: String,
        default: 'rgba(0,0,0,0.3)'
    },

    // Côté gauche personnalisé
    leftSideTitle: {
        type: String,
        default: ''
    },
    leftSideAuthor: {
        type: String,
        default: ''
    },

    // Contenu des pages
    pages: {
        type: Array,
        default: () => ['<p>Contenu par défaut du livre...</p>']
    },

    // Comportement
    hoverEffect: {
        type: Boolean,
        default: true
    },
    clickToOpen: {
        type: Boolean,
        default: true
    },
    initialView: {
        type: String,
        default: 'default',
        validator: (value) => ['default', 'inside', 'back'].includes(value)
    }
})

// Events
const emit = defineEmits(['book-opened', 'book-closed', 'page-changed', 'book-clicked'])

const subText=(text)=>{
    return text?.substring(0,25)
}

const currentPage = ref(0)
const bookState = ref(props.initialView)
const showPages = computed(() => bookState.value === 'inside')

// Classes CSS calculées
const bookClasses = computed(() => [
    props.bookStyle,
    `bk-view${bookState.value}`,
    {
        'bk-bookdefault': bookState.value === 'default' && props.hoverEffect
    }
])

const coverStyle = computed(() => {
    const styles = {
        backgroundColor: props.backgroundColor
    }

    if (props.coverImage) {
        styles.backgroundImage = `url(${props.coverImage})`
        styles.backgroundRepeat = 'no-repeat'
        styles.backgroundPosition = props.coverImagePosition
        styles.backgroundSize = props.coverImageSize
    }

    return styles
})

const backStyle = computed(() => {
    return {
        backgroundColor: props.backColor || props.backgroundColor
    }
})


const leftSideStyle = computed(() => {
    return {
        backgroundColor: props.leftSideColor || props.backgroundColor
    }
})

// Gestionnaires d'événements
const handleMouseEnter = () => {
    if (props.hoverEffect && bookState.value === 'default') {
    }
}

const handleMouseLeave = () => {
    if (props.hoverEffect && bookState.value === 'default') {
    }
}

const showBack = () => {
    bookState.value = 'back'
}

defineExpose({
    showBack,
    currentPage: () => currentPage.value,
    bookState: () => bookState.value
})

</script>

<style scoped>
.bk-book {
    position: relative;
    width: 100%;
    height: 100%;
    transform-style: preserve-3d;
    transition: transform 0.5s;
    margin-bottom: 20px;
    cursor: pointer;
}

.bk-book.bk-bookdefault:hover {
    transform: rotate3d(0,1,0,35deg);
}

.bk-book > div,
.bk-front > div {
    display: block;
    position: absolute;
}

.bk-front {
    transform-style: preserve-3d;
    transform-origin: 0% 50%;
    transition: transform 0.5s;
    transform: translate3d(0,0,20px);
    z-index: 10;
}

.bk-front > div {
    z-index: 1;
    backface-visibility: hidden;
    transform-style: preserve-3d;
}

.bk-page {
    transform: translate3d(0,0,19px);
    display: none;
    width: 295px;
    height: 390px;
    top: 5px;
    backface-visibility: hidden;
    z-index: 9;
}

.bk-viewinside .bk-page {
    display: block;
}

.bk-front,
.bk-back,
.bk-front > div {
    width: 277px;
    height: 370px;
}

.bk-left,
.bk-right {
    width: 40px;
    left: -20px;
}

.bk-top,
.bk-bottom {
    width: 295px;
    height: 40px;
    top: -15px;
    backface-visibility: hidden;
}

.bk-back {
    transform: rotate3d(0,1,0,-180deg) translate3d(0,0,20px);
    box-shadow: 10px 10px 30px rgba(0,0,0,0.3);
    border-radius: 3px 0 0 3px;
}

.bk-cover-back {
    background-color: #000;
    transform: rotate3d(0,1,0,-179deg);
}

.bk-right {
    height: 370px;
    top: 5px;
    transform: rotate3d(0,1,0,90deg) translate3d(0,0,295px);
    backface-visibility: hidden;
}

.bk-left {
    height: 370px;
    transform: rotate3d(0,1,0,-90deg);
}

.bk-top {
    transform: rotate3d(1,0,0,90deg);
}

.bk-bottom {
    transform: rotate3d(1,0,0,-90deg) translate3d(0,0,390px);
}

/* Transform classes */
.bk-viewinside .bk-front {
    transform: translate3d(0,0,20px) rotate3d(0,1,0,-160deg);
}

.bk-book.bk-viewinside {
    transform: translate3d(0,0,150px) rotate3d(0,1,0,0deg);
}

.bk-book.bk-viewback {
    transform: translate3d(0,0,0px) rotate3d(0,1,0,180deg);
}

/* Main colors and content */
.bk-page,
.bk-right,
.bk-top,
.bk-bottom {
    background-color: #fff;
}

.bk-front > div {
    border-radius: 0 3px 3px 0;
    box-shadow: inset 4px 0 10px rgba(0, 0, 0, 0.1);
}

.bk-front:after {
    content: '';
    position: absolute;
    top: 1px;
    bottom: 1px;
    left: -1px;
    width: 1px;
}

.bk-cover:after,
.bk-back:after {
    content: '';
    position: absolute;
    top: 0;
    left: 10px;
    bottom: 0;
    width: 3px;
    background: rgba(0,0,0,0.06);
    box-shadow: 1px 0 3px rgba(255, 255, 255, 0.1);
}

.bk-back:after {
    left: auto;
    right: 10px;
}

/* Couverture prend toute la taille */
.bk-cover {
    width: 100%;
    height: 100%;
    position: relative;
    border-radius: 0 3px 3px 0;
    overflow: hidden;
}

.bk-cover-content {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 30px;
    background-size: contain;
    background: v-bind(coverOverlay ? coverOverlayColor : 'transparent');
    z-index: 2;
}

.bk-cover h2 {
    margin: 0;
    color: #fff;
    text-shadow: 0 -1px 0 rgba(0,0,0,0.1);
}

.bk-author {
    display: block;
    text-transform: uppercase;
    font-weight: 400;
    font-size: 13px;
    margin-bottom: 5px;
}

.bk-title {
    display: block;
    font-family: "Big Caslon", "Book Antiqua", "Palatino Linotype", Georgia, serif;
    font-size: 18px;
}

/* Côté gauche personnalisé */
.bk-left h2 {
    width: 370px;
    height: 40px;
    transform-origin: 0 0;
    transform: rotate(-90deg) translateX(-370px);
    color: #fff;
    font-size: 15px;
    line-height: 40px;
    padding-left: 10px;
    margin: 0;
}

.bk-left .bk-author {
    font-size: 11px;
    padding-right: 20px;
}

.bk-left .bk-title {
    font-size: 14px;
}

/* Contenu du dos personnalisé */
.bk-back-content {
    padding: 40px;
    text-align: center;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.bk-back p {
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    margin: 0;
}

.bk-back img {
    max-width: 200px;
    display: block;
    margin: 0 auto 20px;
}

.bk-content {
    position: absolute;
    top: 30px;
    left: 20px;
    bottom: 20px;
    right: 20px;
    padding: 30px;
    overflow: hidden;
    background: #fff;
    opacity: 0;
    pointer-events: none;
    backface-visibility: hidden;
    transition: opacity 0.3s ease-in-out;
    cursor: default;
}

.bk-content-current {
    opacity: 1;
    pointer-events: auto;
}

.bk-content p {
    padding: 0 0 10px;
    color: #000;
    font-size: 13px;
    line-height: 20px;
    text-align: justify;
    user-select: none;
}

.bk-nav {
    display: block;
    text-align: center;
    margin-top: 20px;
    position: relative;
    z-index: 100;
    cursor: pointer;
}

.bk-nav span {
    display: inline-block;
    width: 20px;
    height: 20px;
    margin: 0 2px;
    color: #aaa;
    background: #f0f0f0;
    border-radius: 50%;
    line-height: 20px;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.3s;
}

.bk-nav span:hover,
.bk-nav span.active {
    background: #007bff;
    color: white;
}

/* Styles par défaut (book-1) */
.book-1 .bk-front:after {
    background-color: v-bind(backgroundColor);
}

/* Style book-2 */
.book-2 .bk-cover-content {
    background: rgba(0,0,0,0.7);
}

.book-2 .bk-cover h2,
.book-2 .bk-left h2 {
    color: #fff;
    font-family: 'Arial', Georgia, serif;
    font-weight: 400;
}

.book-2 .bk-back p {
    color: red;
    font-size: 13px;
    font-family: "Big Caslon", "Book Antiqua", "Palatino Linotype", Georgia, serif;
}

/* Style book-3 */
.book-3 .bk-cover-content {
    position: absolute;
    top: 40px;
    bottom: auto;
    background: transparent;
    padding-top: 15px;
}

.book-3 .bk-back p {
    color: #fff;
    font-size: 13px;
    font-family: Georgia, Times, "Times New Roman", serif;
    text-shadow: 0 -1px 0 rgba(0,0,0,0.1);
}
</style>
